// src/pages/ChatPage.jsx — Epic Games Store style
import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import { createChatRoom, sendMessage, subscribeToMessages, markAsRead } from "../services/chatService";
import { getOrder } from "../services/orderService";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguagesContext";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
.cp { height:100vh; background:#121212; color:#fff; font-family:'Inter',sans-serif; display:flex; flex-direction:column; }
.cp-hdr {
  background:#1a1a1a; border-bottom:1px solid rgba(255,255,255,0.07);
  padding:0 24px; height:64px; margin-top:60px; flex-shrink:0;
  display:flex; align-items:center; gap:14px;
}
.cp-back { color:#6b6b6b; text-decoration:none; font-size:20px; transition:color 0.15s; padding:4px; }
.cp-back:hover { color:#fff; }
.cp-avatar { width:40px; height:40px; background:#0078F2; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.cp-info { flex:1; }
.cp-name { font-size:15px; font-weight:700; color:#fff; margin-bottom:2px; }
.cp-status { font-size:12px; color:#6b6b6b; display:flex; align-items:center; gap:5px; }
.cp-online-dot { width:7px; height:7px; background:#22c55e; border-radius:50%; animation:blink 2s infinite; }
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.4} }
.cp-order-pill { background:rgba(0,120,242,0.1); border:1px solid rgba(0,120,242,0.25); border-radius:4px; padding:5px 12px; font-size:12px; font-weight:600; color:#0078F2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:200px; }

.cp-msgs { flex:1; overflow-y:auto; padding:20px 24px; display:flex; flex-direction:column; gap:10px; }
.cp-msgs::-webkit-scrollbar { width:4px; }
.cp-msgs::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.08); border-radius:2px; }

.date-sep { align-self:center; font-size:11px; font-weight:500; color:#4a4a4a; background:rgba(255,255,255,0.04); border-radius:20px; padding:4px 14px; letter-spacing:0.5px; }

.msg-row { display:flex; gap:9px; max-width:78%; }
.msg-row.mine { align-self:flex-end; flex-direction:row-reverse; }
.msg-row.theirs { align-self:flex-start; }
.msg-av { width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; margin-top:2px; }
.msg-av.admin { background:#0078F2; }
.msg-av.user  { background:#2a2a2a; border:1px solid rgba(255,255,255,0.1); }
.msg-col { display:flex; flex-direction:column; gap:3px; }
.msg-row.mine .msg-col { align-items:flex-end; }
.msg-from { font-size:11px; font-weight:500; color:#4a4a4a; }
.msg-bub { padding:10px 14px; border-radius:10px; font-size:14px; line-height:1.55; font-weight:400; max-width:100%; word-break:break-word; }
.msg-row.mine   .msg-bub { background:#0078F2; color:#fff; border-radius:10px 10px 2px 10px; }
.msg-row.theirs .msg-bub { background:#1a1a1a; border:1px solid rgba(255,255,255,0.08); color:#e0e0e0; border-radius:10px 10px 10px 2px; }
.msg-time { font-size:11px; color:#3a3a3a; font-weight:400; }

.sys-msg { align-self:center; font-size:12px; color:#4a4a4a; background:rgba(255,255,255,0.04); border-radius:20px; padding:5px 14px; }

.cp-empty { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; color:#4a4a4a; text-align:center; padding:40px; }
.cp-empty-ico { font-size:48px; }
.cp-empty-t { font-size:18px; font-weight:700; color:#6b6b6b; }
.cp-empty-d { font-size:14px; color:#4a4a4a; line-height:1.6; white-space:pre-line; }

.cp-inp-area { padding:12px 20px 16px; border-top:1px solid rgba(255,255,255,0.07); background:#1a1a1a; flex-shrink:0; }
.cp-inp-row { display:flex; gap:9px; align-items:flex-end; }
.cp-textarea {
  flex:1; background:#121212; border:1px solid rgba(255,255,255,0.1); border-radius:8px;
  padding:11px 14px; color:#fff; font-family:'Inter',sans-serif; font-size:14px; font-weight:400;
  resize:none; outline:none; transition:border-color 0.15s; min-height:44px; max-height:120px; line-height:1.5;
}
.cp-textarea:focus { border-color:#0078F2; }
.cp-textarea::placeholder { color:#3a3a3a; }
.cp-send { width:44px; height:44px; background:#0078F2; border:none; border-radius:8px; color:#fff; font-size:16px; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all 0.15s; flex-shrink:0; }
.cp-send:hover { background:#0a84ff; transform:scale(1.05); }
.cp-send:disabled { background:#2a2a2a; color:#4a4a4a; cursor:not-allowed; transform:none; }
.cp-hint { font-size:11px; color:#3a3a3a; margin-top:6px; text-align:center; }

.skel { background:#1a1a1a; border-radius:10px; animation:shim 1.5s ease infinite; }
@keyframes shim { 0%,100%{opacity:.5} 50%{opacity:.8} }

@media(max-width:480px){ .msg-row { max-width:90%; } .cp-order-pill { display:none; } }
`;

export default function ChatPage() {
  const { orderId } = useParams();
  const { user, isAdmin } = useAuth();
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!orderId || !user) return;

    getOrder(orderId)
      .then(async (o) => {
        setOrder(o);
        if (o) {
          try {
            await createChatRoom(o);
          } catch (err) {
            console.error("Failed to create chat room:", err);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load order:", err);
        setLoading(false);
      });

    const unsub = subscribeToMessages(orderId, (msgs) => {
      setMessages(msgs);
      markAsRead(orderId, isAdmin ? "admin" : "user");
    });
    return unsub;
  }, [orderId, user, isAdmin]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  async function handleSend() {
    if (!text.trim() || sending) return;
    setSending(true);
    try { await sendMessage(orderId, text.trim(), user, isAdmin?"admin":"user"); setText(""); inputRef.current?.focus(); }
    catch(e) { console.error(e); } finally { setSending(false); }
  }
  function handleKey(e) { if (e.key==="Enter"&&!e.shiftKey) { e.preventDefault(); handleSend(); } }
  function fmtTime(ts) { if (!ts) return ""; return new Date(typeof ts==="number"?ts:ts).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit",hour12:false}); }
  function fmtDate(ts) { if (!ts) return ""; const d=new Date(typeof ts==="number"?ts:ts), t=new Date(); return d.toDateString()===t.toDateString()?"Today":d.toLocaleDateString("en-US",{month:"short",day:"numeric"}); }

  const grouped = [];
  let lastD = null;
  messages.forEach(msg => {
    const d = fmtDate(msg.timestamp);
    if (d !== lastD) { grouped.push({ type:"date", label:d, id:"d_"+d }); lastD=d; }
    grouped.push(msg);
  });

  return (
    <>
      <style>{S}</style>
      <div className="cp">
        <Navbar />
        <div className="cp-hdr">
          <Link to="/orders" className="cp-back">←</Link>
          <div className="cp-avatar">🎮</div>
          <div className="cp-info">
            <div className="cp-name">{t("chat_support")}</div>
            <div className="cp-status"><span className="cp-online-dot" />{loading?"Loading...":t("chat_online")}</div>
          </div>
          {!loading && order && <div className="cp-order-pill">🎮 {order.gameTitle}</div>}
        </div>

        <div className="cp-msgs">
          {loading ? [...Array(3)].map((_,i) => <div key={i} style={{alignSelf:i%2===0?"flex-start":"flex-end",width:"55%"}}><div className="skel" style={{height:52}} /></div>)
          : messages.length === 0 ? (
            <div className="cp-empty">
              <div className="cp-empty-ico">💬</div>
              <div className="cp-empty-t">{t("chat_empty_title")}</div>
              <div className="cp-empty-d">{t("chat_empty_sub")}</div>
            </div>
          ) : (
            <>
              <div className="sys-msg">🎮 {order?.gameTitle || orderId?.slice(0,12)}</div>
              {grouped.map(item => {
                if (item.type==="date") return <div key={item.id} className="date-sep">{item.label}</div>;
                const isMe = item.senderId===user?.uid;
                const isAdm = item.role==="admin";
                return (
                  <div key={item.id} className={`msg-row ${isMe?"mine":"theirs"}`}>
                    {!isMe && <div className={`msg-av ${isAdm?"admin":"user"}`}>{isAdm?"⚡":(item.senderName||"U")[0].toUpperCase()}</div>}
                    <div className="msg-col">
                      {!isMe && <div className="msg-from">{isAdm?t("chat_support"):item.senderName}</div>}
                      <div className="msg-bub">{item.text}</div>
                      <div className="msg-time">{fmtTime(item.timestamp)}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </>
          )}
        </div>

        <div className="cp-inp-area">
          <div className="cp-inp-row">
            <textarea ref={inputRef} className="cp-textarea" placeholder={t("chat_placeholder")} value={text} onChange={e=>setText(e.target.value)} onKeyDown={handleKey} rows={1} />
            <button className="cp-send" onClick={handleSend} disabled={!text.trim()||sending}>➤</button>
          </div>
          <div className="cp-hint">{t("chat_hint")}</div>
        </div>
      </div>
    </>
  );
}